import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";

import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";

import { trpc } from "@/lib/trpc";
import { createRouterContext } from "@/lib/router-context";
import { isTouchDevice } from "@/lib/device";

import { registerSW } from "virtual:pwa-register";

import { routeTree } from "./routeTree.gen";
import { ThemeProvider } from "./components/theme/theme-provider";


import "./index.css";
import { getSession } from "./lib/session";

  const session = getSession();

Sentry.init({
  dsn:"https://635971544febeeff3b629cfc30320dac@o4511525679333376.ingest.us.sentry.io/4511525688901632",
  // dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: true,
  environment: import.meta.env.MODE,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // NOTE: This will disable built-in masking. Only use this if your site has no sensitive data, or if you've already set up other options for masking or blocking relevant data, such as 'ignore', 'block', 'mask' and 'maskFn'.
      maskAllText: false,
      blockAllMedia: false,
    })
  ],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.05,
  replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,

});
Sentry.setUser({
  id: session?.userId,
});
Sentry.setTags({
  workspaceId: session?.workspaceId ?? "none",
  role: session?.role ?? "unknown",
  plan: session?.plan ?? "free",
});

const routerContext = createRouterContext();

const preloadMode = isTouchDevice() ? "viewport" : "intent";

if (import.meta.env.PROD) {
  registerSW({
    immediate: true,
  });
} else if (
  typeof window !== "undefined" &&
  "serviceWorker" in navigator
) {
  void navigator.serviceWorker
    .getRegistrations()
    .then((registrations) =>
      Promise.all(
        registrations.map((registration) =>
          registration.unregister(),
        ),
      ),
    )
    .catch(() => undefined);
}

export const router = createRouter({
  routeTree,
  context: routerContext,
  notFoundMode: "root",
  defaultPreload: preloadMode,
  defaultPreloadDelay: 0,
  defaultPreloadStaleTime: 0,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

ReactDOM.createRoot(
  document.getElementById("root")!,
).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={<div>Something went wrong.</div>}
    >
      <ThemeProvider
        defaultTheme="system"
        storageKey="Quire-ui-theme"
      >
        <trpc.Provider
          client={routerContext.trpcClient}
          queryClient={routerContext.queryClient}
        >
          <QueryClientProvider
            client={routerContext.queryClient}
          >
            <RouterProvider router={router} />
          </QueryClientProvider>
        </trpc.Provider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
